import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import defaultSiteData from './data/siteData';
import { trackEvent } from './utils/analytics';
import { fetchSiteData } from './api/fetchSiteData';
import CorporateStandard from './templates/CorporateStandard';
import ServiceLP from './templates/ServiceLP';
import BrandVisual from './templates/BrandVisual';
import StartupModern from './templates/StartupModern';
import LocalBusiness from './templates/LocalBusiness';
import Footer from './components/Footer';
import TermsOfService from './pages/TermsOfService';

const TEMPLATES = [
  { key: 'corporate', name: 'Corporate Standard', desc: 'コーポレート・企業サイト向け', Component: CorporateStandard },
  { key: 'service',   name: 'Service LP',         desc: 'サービス紹介・LP向け',         Component: ServiceLP },
  { key: 'brand',     name: 'Brand Visual',       desc: 'ブランド・クリエイティブ向け',   Component: BrandVisual },
  { key: 'startup',   name: 'Startup Modern',     desc: 'スタートアップ・テック向け',     Component: StartupModern },
  { key: 'local',     name: 'Local Business',     desc: '店舗・地域ビジネス向け',         Component: LocalBusiness },
];

const DOT_COLORS = ['#1e3a5f', '#6b21a8', '#111', '#06b6d4', '#166534'];
const TEMPLATE_KEY_MAP = Object.fromEntries(TEMPLATES.map((t, i) => [t.key, i]));

const T_W = 1440;
const C_W = 380;
const C_H = 254;
const SCALE = C_W / T_W;

const L_W = 570;

const FONT_SETS = {
  minimal: {
    name: 'ミニマル',
    heading: "'Shippori Mincho', serif",
    body: "'DM Sans', system-ui, sans-serif",
  },
  modern: {
    name: 'モダン',
    heading: "'Noto Sans JP', sans-serif",
    body: "'Inter', system-ui, sans-serif",
  },
  classic: {
    name: 'クラシック',
    heading: "'Zen Old Mincho', serif",
    body: "'Cormorant Garamond', Georgia, serif",
  },
};

// Google Fonts URL covering all font sets
const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Shippori+Mincho:wght@400;500;600' +
  '&family=DM+Sans:wght@300;400;500' +
  '&family=Noto+Sans+JP:wght@300;400;500' +
  '&family=Inter:wght@300;400;500' +
  '&family=Zen+Old+Mincho:wght@400;500;700' +
  '&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400' +
  '&display=swap';

function MainApp() {
  const [screen, setScreen]               = useState('input');
  const [activeSiteData, setActiveSiteData] = useState(defaultSiteData);
  const [activeUrl, setActiveUrl]         = useState('');
  const [errorMsg, setErrorMsg]           = useState(null);
  const [modalIdx, setModalIdx]           = useState(null);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [selectedFont, setSelectedFont]   = useState('minimal');

  // Inject Google Fonts link on mount
  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = GOOGLE_FONTS_URL;
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    if (modalIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape')     setModalIdx(null);
      if (e.key === 'ArrowRight') setModalIdx(i => (i + 1) % TEMPLATES.length);
      if (e.key === 'ArrowLeft')  setModalIdx(i => (i - 1 + TEMPLATES.length) % TEMPLATES.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalIdx]);

  const handleSubmit = async (url) => {
    setScreen('loading');
    setLoadingComplete(false);
    setErrorMsg(null);
    try {
      const data = await fetchSiteData(url);
      setActiveSiteData(data);
      setActiveUrl(url);
      setModalIdx(null);
      trackEvent('generate_complete');
      setLoadingComplete(true);
    } catch (err) {
      console.error('[App] handleSubmit APIエラー:', err.message ?? err);
      setErrorMsg('取得できませんでした。別のURLを試してください。');
      setLoadingComplete(false);
      setScreen('input');
    }
  };

  const handleBack = () => {
    setModalIdx(null);
    setErrorMsg(null);
    setScreen('input');
  };

  const handleTemplateClick = (idx) => {
    trackEvent('template_view', { template: TEMPLATES[idx].key });
    setModalIdx(idx);
  };

  const handleFontChange = (font) => {
    trackEvent('font_change', { font });
    setSelectedFont(font);
  };

  if (screen === 'input')   return <InputScreen onSubmit={handleSubmit} errorMsg={errorMsg} />;
  if (screen === 'loading') return (
    <LoadingScreen
      complete={loadingComplete}
      onDone={() => setScreen('results')}
      onTimeout={() => {
        setLoadingComplete(false);
        setErrorMsg('このURLは取得できませんでした。別のURLをお試しください。');
        setScreen('input');
      }}
    />
  );

  // results screen
  const recIdx = TEMPLATE_KEY_MAP[activeSiteData.recommendedTemplate] ?? 0;
  const otherTemplates = TEMPLATES
    .map((t, i) => ({ ...t, globalIdx: i }))
    .filter((_, i) => i !== recIdx);
  const fontSet = FONT_SETS[selectedFont];

  return (
    <div className="app-results" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <style>{`
        @media (max-width: 768px) {
          .app-results-inner { padding: 40px 16px !important; }
          .app-others-grid { grid-template-columns: 1fr !important; align-items: center; }
          .app-modal-bar { padding: 12px 16px !important; }
          .app-modal-bar-title { font-size: 15px !important; }
          .app-modal-bar-desc { display: none; }
          .app-nav-arrow { display: none !important; }
        }
      `}</style>

      <div className="app-results-inner" style={{ flex: 1, padding: '60px 40px' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ marginBottom: 16 }}>
          <span style={{
            display: 'inline-block', padding: '5px 14px',
            background: '#fef9c3', border: '1px solid #fde047',
            borderRadius: 20, color: '#854d0e', fontSize: 13, fontWeight: 600,
          }}>
            ※ 表示内容はイメージ確認用の仮コピーです
          </span>
        </div>
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={handleBack}
            style={{
              background: 'none', border: '1px solid #cbd5e1', color: '#64748b',
              padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            }}
          >
            ← 別のURLを試す
          </button>
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: '#0f172a', marginBottom: 6, letterSpacing: '-0.02em' }}>
          {activeSiteData.siteName}
        </h1>
        {activeUrl && (
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>{activeUrl}</p>
        )}
        <p style={{ color: '#64748b', fontSize: 15 }}>
          カードをクリックしてテンプレートをプレビュー　　←→キーで切り替え　　ESCで閉じる
        </p>
      </div>

      {/* Recommended section */}
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <span style={{
          display: 'inline-block', padding: '5px 16px',
          background: '#fef3c7', border: '1px solid #f59e0b',
          borderRadius: 20, color: '#92400e', fontSize: 13, fontWeight: 700,
          marginBottom: 12,
        }}>
          ✨ おすすめ
        </span>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
          {TEMPLATES[recIdx].name}
        </h2>
        {activeSiteData.recommendedReason && (
          <p style={{ color: '#64748b', fontSize: 15, maxWidth: 560, margin: '0 auto 28px' }}>
            {activeSiteData.recommendedReason}
          </p>
        )}
        <RecommendedCard
          template={TEMPLATES[recIdx]}
          index={recIdx}
          activeSiteData={activeSiteData}
          fontSet={fontSet}
          onClick={() => handleTemplateClick(recIdx)}
        />
      </div>

      {/* Others section */}
      <div>
        <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#475569', marginBottom: 28, letterSpacing: '0.02em' }}>
          他のデザインパターン
        </h2>
        <div
          className="app-others-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: 24, justifyContent: 'center' }}
        >
          {otherTemplates.map((t) => (
            <TemplateCard
              key={t.globalIdx}
              template={t}
              index={t.globalIdx}
              activeSiteData={activeSiteData}
              fontSet={fontSet}
              onClick={() => handleTemplateClick(t.globalIdx)}
            />
          ))}
        </div>
      </div>

      </div>

      <Footer />

      {modalIdx !== null && (
        <Modal
          idx={modalIdx}
          activeSiteData={activeSiteData}
          fontSet={fontSet}
          selectedFont={selectedFont}
          onFontChange={handleFontChange}
          onClose={() => setModalIdx(null)}
          onPrev={() => setModalIdx(i => (i - 1 + TEMPLATES.length) % TEMPLATES.length)}
          onNext={() => setModalIdx(i => (i + 1) % TEMPLATES.length)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function InputScreen({ onSubmit, errorMsg }) {
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      setUrlError('http:// または https:// で始まるURLを入力してください');
      return;
    }
    setUrlError('');
    trackEvent('generate_start', { url: trimmed });
    onSubmit(trimmed);
  };

  const steps = [
    { label: 'URLを入力' },
    { label: 'サイトを解析' },
    { label: '推薦+5パターン表示' },
  ];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: '#f1f5f9', fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: 600, textAlign: 'center' }}>
        <h1 style={{ fontSize: 38, fontWeight: 800, color: '#0f172a', marginBottom: 12, letterSpacing: '-0.03em' }}>
          サイトモックアップ
        </h1>
        <p style={{ color: '#64748b', fontSize: 17, lineHeight: 1.7, marginBottom: 40 }}>
          URLを入力すると、サイト情報を自動取得して<br />
          最適テンプレートを推薦＋5種類で生成します
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: 48 }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ textAlign: 'center', padding: '0 20px' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: '#6366f1', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 700, margin: '0 auto 8px',
                }}>
                  {i + 1}
                </div>
                <p style={{ color: '#475569', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{step.label}</p>
              </div>
              {i < steps.length - 1 && (
                <div style={{ width: 32, height: 1, background: '#cbd5e1', flexShrink: 0 }} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setUrlError(''); }}
              placeholder="https://www.example.com"
              style={{
                flex: 1, padding: '14px 18px', fontSize: 16,
                border: `2px solid ${urlError ? '#ef4444' : '#e2e8f0'}`, borderRadius: 10,
                outline: 'none', background: '#fff', color: '#0f172a',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => (e.target.style.borderColor = urlError ? '#ef4444' : '#6366f1')}
              onBlur={(e) => (e.target.style.borderColor = urlError ? '#ef4444' : '#e2e8f0')}
            />
            <button
              type="submit"
              disabled={!url.trim()}
              style={{
                padding: '14px 28px', fontSize: 16, fontWeight: 700,
                background: url.trim() ? '#6366f1' : '#c7d2fe',
                color: '#fff', border: 'none', borderRadius: 10,
                cursor: url.trim() ? 'pointer' : 'not-allowed',
                transition: 'background 0.15s', whiteSpace: 'nowrap',
              }}
            >
              生成する
            </button>
          </div>

          {urlError && (
            <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8, textAlign: 'left' }}>{urlError}</p>
          )}
          {errorMsg && !urlError && (
            <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8, textAlign: 'left' }}>{errorMsg}</p>
          )}
        </form>

        <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 20 }}>
          ※ Claude API（web_search付き）でサイト情報を取得します
        </p>
      </div>
      </div>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------

const LOADING_STEPS = [
  'ページ情報を取得中...',
  '画像を取得中...',
  '情報を整理中...',
  'テンプレートに反映中...',
];

const PROGRESS_MILESTONES = [
  { t: 500,  p: 15 },
  { t: 2000, p: 35 },
  { t: 4000, p: 55 },
  { t: 6000, p: 75 },
  { t: 8000, p: 90 },
];

const SUB_MESSAGES = [
  'サイトのデザインテイストを読み取っています',
  '業種・ターゲットを分析しています',
  '最適なレイアウトを選定しています',
  'テキストをページ向けに最適化しています',
  'カラーパレットを抽出しています',
  '5つのテンプレートに当てはめています',
];

function LoadingScreen({ complete, onDone, onTimeout }) {
  const [progress, setProgress]       = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [msgIdx, setMsgIdx]           = useState(0);
  const [msgVisible, setMsgVisible]   = useState(true);
  const [slowWarning, setSlowWarning] = useState(false);

  const onDoneRef    = useRef(onDone);
  const onTimeoutRef = useRef(onTimeout);
  onDoneRef.current    = onDone;
  onTimeoutRef.current = onTimeout;
  const msgTimerRef  = useRef(null);

  useEffect(() => {
    const timers = [
      ...PROGRESS_MILESTONES.map(({ t, p }) =>
        setTimeout(() => setProgress(prev => Math.max(prev, p)), t)
      ),
      setTimeout(() => setCurrentStep(1), 2000),
      setTimeout(() => setCurrentStep(2), 4000),
      setTimeout(() => setCurrentStep(3), 6000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!complete) return;
    setProgress(100);
    const t = setTimeout(() => onDoneRef.current(), 300);
    return () => clearTimeout(t);
  }, [complete]);

  useEffect(() => {
    const id = setInterval(() => {
      setMsgVisible(false);
      msgTimerRef.current = setTimeout(() => {
        setMsgIdx(i => (i + 1) % SUB_MESSAGES.length);
        setMsgVisible(true);
      }, 300);
    }, 2000);
    return () => {
      clearInterval(id);
      if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    };
  }, []);

  // 50秒で警告メッセージ、70秒で自動的に入力画面へ
  useEffect(() => {
    const t50 = setTimeout(() => setSlowWarning(true), 50000);
    const t70 = setTimeout(() => onTimeoutRef.current?.(), 70000);
    return () => { clearTimeout(t50); clearTimeout(t70); };
  }, []);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#f1f5f9', fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '40px 20px',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        width: 48, height: 48, border: '4px solid #e2e8f0',
        borderTopColor: '#6366f1', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite', marginBottom: 40,
      }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 280, marginBottom: 36 }}>
        {LOADING_STEPS.map((label, i) => {
          const done   = i < currentStep;
          const active = i === currentStep;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
                background: done ? '#6366f1' : active ? '#e0e7ff' : '#e2e8f0',
                color: done ? '#fff' : active ? '#6366f1' : '#94a3b8',
                transition: 'background 0.3s, color 0.3s',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: 15, fontWeight: active ? 600 : 400,
                color: done ? '#6366f1' : active ? '#0f172a' : '#94a3b8',
                transition: 'color 0.3s',
              }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ width: 280, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginBottom: 28 }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: 'linear-gradient(90deg, #6366f1, #818cf8)',
          borderRadius: 3, transition: 'width 0.6s ease',
        }} />
      </div>

      <p style={{
        color: '#94a3b8', fontSize: 14, textAlign: 'center',
        opacity: msgVisible ? 1 : 0, transition: 'opacity 0.3s ease', minHeight: 20,
      }}>
        {SUB_MESSAGES[msgIdx]}
      </p>

      {slowWarning && (
        <p style={{
          marginTop: 24, color: '#f59e0b', fontSize: 13, textAlign: 'center',
          maxWidth: 320, lineHeight: 1.6,
        }}>
          取得に時間がかかっています。<br />
          サイトによっては対応できない場合があります。
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RecommendedCard({ template, index, activeSiteData, fontSet, onClick }) {
  const [hovered, setHovered] = useState(false);
  const { Component } = template;

  const cardW = Math.min(L_W, (window.innerWidth || L_W) - 48);
  const cardH = Math.round(cardW * C_H / C_W);
  const cardScale = cardW / T_W;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: cardW, background: '#fff', borderRadius: 16, overflow: 'hidden',
          boxShadow: hovered ? '0 24px 64px rgba(0,0,0,0.22)' : '0 6px 32px rgba(0,0,0,0.13)',
          cursor: 'pointer',
          transform: hovered ? 'translateY(-6px)' : 'none',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          border: '2px solid #f59e0b',
        }}
      >
        <div style={{ position: 'relative', width: cardW, height: cardH, overflow: 'hidden' }}>
          <div style={{
            width: T_W, height: Math.round(cardH / cardScale),
            transform: `scale(${cardScale})`, transformOrigin: 'top left',
            pointerEvents: 'none', userSelect: 'none',
          }}>
            <Component data={activeSiteData} fontSet={fontSet} />
          </div>
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.48)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease',
          }}>
            <span style={{ background: '#fff', color: '#0f172a', padding: '12px 40px', borderRadius: 8, fontWeight: 700, fontSize: 16 }}>
              詳しく見る
            </span>
          </div>
        </div>

        <div style={{ padding: '14px 20px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: DOT_COLORS[index] }} />
          <span style={{ color: '#0f172a', fontSize: 16, fontWeight: 700 }}>{template.name}</span>
          <span style={{ color: '#64748b', fontSize: 13, marginLeft: 4 }}>{template.desc}</span>
        </div>
      </div>

      <button
        onClick={onClick}
        style={{
          marginTop: 16, padding: '12px 56px',
          background: '#f59e0b', color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = '#d97706')}
        onMouseLeave={e => (e.currentTarget.style.background = '#f59e0b')}
      >
        詳しく見る
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TemplateCard({ template, index, activeSiteData, fontSet, onClick }) {
  const [hovered, setHovered] = useState(false);
  const { name, desc, Component } = template;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: C_W, background: '#fff', borderRadius: 14, overflow: 'hidden',
        boxShadow: hovered ? '0 16px 48px rgba(0,0,0,0.18)' : '0 2px 14px rgba(0,0,0,0.09)',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-5px)' : 'none',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        flexShrink: 0,
      }}
    >
      <div style={{ position: 'relative', width: C_W, height: C_H, overflow: 'hidden' }}>
        <div style={{
          width: T_W, height: Math.round(C_H / SCALE),
          transform: `scale(${SCALE})`, transformOrigin: 'top left',
          pointerEvents: 'none', userSelect: 'none',
        }}>
          <Component data={activeSiteData} fontSet={fontSet} />
        </div>

        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.48)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease',
        }}>
          <span style={{ background: '#fff', color: '#0f172a', padding: '11px 32px', borderRadius: 8, fontWeight: 700, fontSize: 15 }}>
            拡大表示
          </span>
        </div>
      </div>

      <div style={{ padding: '16px 20px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: DOT_COLORS[index] }} />
          <h3 style={{ color: '#0f172a', fontSize: 17, fontWeight: 700 }}>{name}</h3>
        </div>
        <p style={{ color: '#64748b', fontSize: 13 }}>{desc}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FontSelector({ selectedFont, onFontChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: open ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.15)', color: '#fff',
          padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
          fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
          display: 'flex', alignItems: 'center', gap: 5,
          transition: 'background 0.15s',
        }}
      >
        Aa <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, overflow: 'hidden', minWidth: 140,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 10,
        }}>
          {Object.entries(FONT_SETS).map(([key, fs]) => (
            <div
              key={key}
              onClick={() => { onFontChange(key); setOpen(false); }}
              style={{
                padding: '10px 16px', cursor: 'pointer', fontSize: 13,
                color: selectedFont === key ? '#818cf8' : 'rgba(255,255,255,0.75)',
                background: selectedFont === key ? 'rgba(99,102,241,0.15)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (selectedFont !== key) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { if (selectedFont !== key) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 14, opacity: selectedFont === key ? 1 : 0 }}>✓</span>
              {fs.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Modal({ idx, activeSiteData, fontSet, selectedFont, onFontChange, onClose, onPrev, onNext }) {
  const { name, desc, Component } = TEMPLATES[idx];

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}
    >
      {/* Top bar */}
      <div
        onClick={e => e.stopPropagation()}
        className="app-modal-bar"
        style={{
          background: 'rgba(15,23,42,0.95)', padding: '14px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span className="app-modal-bar-title" style={{ color: '#fff', fontSize: 19, fontWeight: 700 }}>{name}</span>
          <span className="app-modal-bar-desc" style={{ color: '#64748b', fontSize: 14 }}>{desc}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <FontSelector selectedFont={selectedFont} onFontChange={onFontChange} />
          <span style={{ color: '#475569', fontSize: 13, marginLeft: 2 }}>{idx + 1} / {TEMPLATES.length}</span>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >✕</button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ flex: 1, overflowY: 'auto', background: '#fff' }}
      >
        <div style={{ maxWidth: 1440, margin: '0 auto' }}>
          <Component data={activeSiteData} fontSet={fontSet} />
        </div>
      </div>

      <NavArrow side="left"  onClick={e => { e.stopPropagation(); onPrev(); }} />
      <NavArrow side="right" onClick={e => { e.stopPropagation(); onNext(); }} />
    </div>
  );
}

function NavArrow({ side, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="app-nav-arrow"
      style={{
        position: 'fixed', [side]: 16, top: '50%', transform: 'translateY(-50%)',
        background: hovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.15)', color: '#fff',
        width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', fontSize: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s ease', zIndex: 1001,
      }}
    >
      {side === 'left' ? '←' : '→'}
    </button>
  );
}

// ---------------------------------------------------------------------------

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </BrowserRouter>
  );
}
